/**
 * App-mode route helpers + entity metadata/name resolution.
 *
 * Item-form *schema* resolution lives in `resolveFormSchema.ts` (the single
 * resolver). This module only knows how to build App routes and how to map
 * a SQL table name back to its Designer object name — no module-specific
 * knowledge, no form-schema fallbacks.
 */
import { ApiService } from '@/utils/api'
import type { ConfigNode } from '../designer/types/config.types'
import { isEntityNode, resolveEntityTableKey, tableNameFromEntityName } from '../designer/utils/entityMetadataKey'
import type { FormSchema } from '../designer/types/form.types'
import { fetchDesignerTree } from './designerTree'

export function appRecordPath(objectName: string, recordId: string): string {
  return `/app/${objectName}/${recordId}`
}

export function appNewRecordPath(objectName: string): string {
  return `/app/${objectName}/new`
}

export type EntityMetadataLite = {
  name?: string
  list_form_schema?: FormSchema | null
  item_form_schema?: FormSchema | null
}

/** Load entity metadata by SQL table_name (resolves design object name). */
export async function loadEntityMetadata(
  connectionId: string,
  tableNameHint: string,
  objectNameHint?: string | null,
): Promise<EntityMetadataLite | null> {
  const tableKey = await resolveEntityTableKey(connectionId, objectNameHint, tableNameHint)
  if (!tableKey) return null
  try {
    const params: Record<string, string> = {
      connection_id: connectionId,
    }
    if (objectNameHint?.trim()) params.object_name = objectNameHint.trim()
    return (await ApiService.get(
      `/v1/designer/entity/${encodeURIComponent(tableKey)}/metadata`,
      { params },
    )) as EntityMetadataLite
  } catch {
    return null
  }
}

async function findDesignObjectNameInTree(
  connectionId: string,
  tableName: string,
): Promise<string | null> {
  try {
    const tree = await fetchDesignerTree(connectionId)
    const tn = tableName.toLowerCase()
    const walk = (nodes: ConfigNode[]): string | null => {
      for (const node of nodes) {
        if (isEntityNode(node)) {
          const nodeTable = (node.table_name || tableNameFromEntityName(node.name || '')).toLowerCase()
          if (nodeTable === tn && node.name) return node.name
        }
        if (node.children?.length) {
          const found = walk(node.children)
          if (found) return found
        }
      }
      return null
    }
    return walk(tree)
  } catch {
    return null
  }
}

/** Resolve configuration object name (e.g. an entity's Designer name) from table_name. */
export async function resolveDesignObjectName(
  connectionId: string,
  tableName: string,
  objectNameHint?: string | null,
): Promise<string | null> {
  if (objectNameHint?.trim()) return objectNameHint.trim()

  const meta = await loadEntityMetadata(connectionId, tableName)
  if (meta?.name?.trim()) return meta.name.trim()

  try {
    const data = await ApiService.get<{ catalogs?: { name: string; table_name: string }[]; documents?: { name: string; table_name: string }[] }>(
      '/v1/designer/entities',
      { params: { connection_id: connectionId, include_excluded: true } },
    )
    const all = [...(data.catalogs || []), ...(data.documents || [])]
    const tn = tableName.toLowerCase()
    const hit = all.find((e) => (e.table_name || '').toLowerCase() === tn)
    if (hit?.name) return hit.name
  } catch {
    // ignore
  }

  const fromTree = await findDesignObjectNameInTree(connectionId, tableName)
  return fromTree
}

export { schemaHasCommandBar, schemaHasRootCommandBar } from './formSchemaCommandBar'
